// Smart Sash Admin Panel - Dashboard Page
// Main dashboard with overview statistics

import { Card, PermissionGate, Spinner } from '@/components/common';
import { HoodUsageSummary } from '@/components/reports';
import { usePermissions } from '@/hooks';
import { UserRoleLabels } from '@/models';
import { useAuth } from '@/services/auth';
import { useDeviceStore, useInstitutionStore, useLabStore, useUserStore } from '@/store';
import React, { useEffect, useState } from 'react';

interface DashboardStats {
  totalInstitutions: number;
  totalUsers: number;
  totalDevices: number;
  totalLabs: number;
  activeDevices: number;
  certifiedUsers: number;
}

/**
 * Dashboard Page Component
 */
export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { canAccessRoute, hasPermission } = usePermissions();
  const [stats, setStats] = useState<DashboardStats>({
    totalInstitutions: 0,
    totalUsers: 0,
    totalDevices: 0,
    totalLabs: 0,
    activeDevices: 0,
    certifiedUsers: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const institutionStore = useInstitutionStore();
  const userStore = useUserStore();
  const deviceStore = useDeviceStore();
  const labStore = useLabStore();

  useEffect(() => {
    const loadDashboardData = async () => {
      setIsLoading(true);
      try {
        // Load data only for entities the current user is permitted to view
        const canViewUsers = hasPermission('User.ViewAll');
        const canViewDevices = hasPermission('Device.ViewAll');
        const canViewLabs = hasPermission('Lab.ViewAll');

        await Promise.all([
          institutionStore.fetchInstitutions(1, 1),
          canViewUsers ? userStore.fetchUsers() : Promise.resolve(),
          canViewDevices ? deviceStore.fetchDevices(1, 1) : Promise.resolve(),
          canViewLabs ? labStore.fetchLabs(1, 1) : Promise.resolve(),
        ]);
        // Read latest state via getState() to avoid stale closure values
        setStats({
          totalInstitutions: useInstitutionStore.getState().pagination?.totalCount || 0,
          totalUsers: canViewUsers ? useUserStore.getState().pagination?.totalCount || 0 : 0,
          totalDevices: canViewDevices ? useDeviceStore.getState().pagination?.totalCount || 0 : 0,
          totalLabs: canViewLabs ? useLabStore.getState().pagination?.totalCount || 0 : 0,
          activeDevices: 0,
          certifiedUsers: 0,
        });
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-0">Dashboard</h1>
              <p className="text-muted">Welcome back, {user?.firstName || user?.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {isLoading ? (
        <div className="text-center py-5">
          <Spinner size="lg" />
          <p className="mt-3 text-muted">Loading dashboard statistics...</p>
        </div>
      ) : (
        <>
          <div className="row g-4 mb-4">
            <PermissionGate permission="Institution.ViewAggregates">
              <div className="col-12 col-sm-6 col-lg-3">
                <Card className="border-0 shadow-sm h-100">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-primary bg-opacity-10 rounded-3 p-3">
                        <i className="fas fa-university fa-2x text-primary"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-1">Total Institutions</h6>
                      <h3 className="mb-0">{stats.totalInstitutions}</h3>
                    </div>
                  </div>
                </Card>
              </div>
            </PermissionGate>

            <PermissionGate permission="User.ViewAll">
              <div className="col-12 col-sm-6 col-lg-3">
                <Card className="border-0 shadow-sm h-100">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-success bg-opacity-10 rounded-3 p-3">
                        <i className="fas fa-users fa-2x text-success"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-1">Total Users</h6>
                      <h3 className="mb-0">{stats.totalUsers}</h3>
                      <small className="text-muted">{stats.certifiedUsers} certified</small>
                    </div>
                  </div>
                </Card>
              </div>
            </PermissionGate>

            <PermissionGate permission="Device.ViewAll">
              <div className="col-12 col-sm-6 col-lg-3">
                <Card className="border-0 shadow-sm h-100">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-warning bg-opacity-10 rounded-3 p-3">
                        <i className="fas fa-laptop fa-2x text-warning"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-1">Total Devices</h6>
                      <h3 className="mb-0">{stats.totalDevices}</h3>
                      <small className="text-muted">{stats.activeDevices} online</small>
                    </div>
                  </div>
                </Card>
              </div>
            </PermissionGate>

            <PermissionGate permission="Lab.ViewAll">
              <div className="col-12 col-sm-6 col-lg-3">
                <Card className="border-0 shadow-sm h-100">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-info bg-opacity-10 rounded-3 p-3">
                        <i className="fas fa-flask fa-2x text-info"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-1">Total Labs</h6>
                      <h3 className="mb-0">{stats.totalLabs}</h3>
                    </div>
                  </div>
                </Card>
              </div>
            </PermissionGate>
          </div>

          {/* Recent Activity / Quick Actions */}
          <div className="row g-4">
            <div className="col-12 col-lg-8">
              <Card title="Quick Actions" className="border-0 shadow-sm">
                <div className="row g-3">
                  {canAccessRoute('/institutions') && (
                    <div className="col-12 col-md-6">
                      <a href="/institutions" className="text-decoration-none">
                        <div className="d-flex align-items-center p-3 border rounded hover-shadow">
                          <i className="fas fa-university fa-2x text-primary me-3"></i>
                          <div>
                            <h6 className="mb-0">Manage Institutions</h6>
                            <small className="text-muted">Add or edit organizations</small>
                          </div>
                        </div>
                      </a>
                    </div>
                  )}
                  {canAccessRoute('/users') && (
                    <div className="col-12 col-md-6">
                      <a href="/users" className="text-decoration-none">
                        <div className="d-flex align-items-center p-3 border rounded hover-shadow">
                          <i className="fas fa-users fa-2x text-success me-3"></i>
                          <div>
                            <h6 className="mb-0">Manage Users</h6>
                            <small className="text-muted">Add or edit user accounts</small>
                          </div>
                        </div>
                      </a>
                    </div>
                  )}
                  {canAccessRoute('/devices') && (
                    <div className="col-12 col-md-6">
                      <a href="/devices" className="text-decoration-none">
                        <div className="d-flex align-items-center p-3 border rounded hover-shadow">
                          <i className="fas fa-laptop fa-2x text-warning me-3"></i>
                          <div>
                            <h6 className="mb-0">Manage Devices</h6>
                            <small className="text-muted">Configure and monitor devices</small>
                          </div>
                        </div>
                      </a>
                    </div>
                  )}
                  {canAccessRoute('/labs') && (
                    <div className="col-12 col-md-6">
                      <a href="/labs" className="text-decoration-none">
                        <div className="d-flex align-items-center p-3 border rounded hover-shadow">
                          <i className="fas fa-flask fa-2x text-info me-3"></i>
                          <div>
                            <h6 className="mb-0">Manage Labs</h6>
                            <small className="text-muted">Configure laboratory spaces</small>
                          </div>
                        </div>
                      </a>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            <div className="col-12 col-lg-4">
              <Card title="System Information" className="border-0 shadow-sm">
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Logged in as:</small>
                  <strong>{user?.email}</strong>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">User Name:</small>
                  <strong>
                    {user?.firstName} {user?.lastName}
                  </strong>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">User Roles:</small>
                  {user?.roles && user.roles.length > 0 ? (
                    <div>
                      {user.roles.map((role, index) => (
                        <span key={index} className="badge bg-primary me-1">
                          {UserRoleLabels[role] ?? role}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted">No roles assigned</span>
                  )}
                </div>
                <hr />
                <div className="d-grid">
                  <a href="/certification" className="btn btn-outline-primary btn-sm">
                    <i className="fas fa-certificate me-2"></i>
                    View Certifications
                  </a>
                </div>
              </Card>
            </div>
          </div>

          {/* Hood Usage Summary */}
          <PermissionGate permission="FullAccess">
            <div className="row g-4 mt-0">
              <div className="col-12">
                <HoodUsageSummary />
              </div>
            </div>
          </PermissionGate>
        </>
      )}
    </div>
  );
};

export default Dashboard;
